import { FormikTouched } from 'formik';
import * as React from 'react';
import { useCallback, useEffect } from 'react';
import * as _ from 'underscore';
import { BaseReactSettingsCtrlProps } from '../../controllers/BaseReactSettingsCtrl';
import { Formik, FormikErrors, FormikHelpers, useFormikContext } from '../../FormikWrapperForStubbing';
import { OuterSiteSettingsForServerAdminProps } from './ReactSiteSettingsForServerAdmin';
import { SettingsComponentRegistry } from './SettingsComponentRegistry';

export const InputDebounceMs = 200;

export interface SectionContainerBaseProps {
  sections: string[];
  settings: object;
}

function wireFormikCallbacks(props: BaseReactSettingsCtrlProps) {
  // get formik specific functionality from the context. This is cleaner than polluting props with an HOC.
  const formik = useFormikContext<BaseReactSettingsCtrlProps>();

  // wrapper callback for submitForm because it has a different signature than what setSaveCallback expects
  const submitFormCallback = useCallback(() => {
    formik.submitForm();
  }, [formik.submitForm]);

  useEffect(() => {
    props.setSaveCallback(submitFormCallback);
    props.setCancelCallback(() => {
      formik.resetForm();
      formik.validateForm();
    });
    props.setOpenNavigationDialogCallback(null);
    props.setIsBusyCallback(() => formik.isSubmitting);
  }, []);

  useEffect(() => {
    props.setIsFormDirty(formik.dirty);
    props.setIsFormValid(formik.isValid);
    props.setIsBusyCallback(() => formik.isSubmitting);
  }, [formik.isSubmitting, formik.dirty, formik.isValid]);

  useEffect(() => {
    props.mergeIntoParentScope?.(formik.values);
  }, [formik.values]);
}

function handleSubmit(values: BaseReactSettingsCtrlProps, actions: FormikHelpers<BaseReactSettingsCtrlProps>) {
  actions.setSubmitting(false);
  actions.resetForm({values});
  actions.validateForm(); // this is needed to enable the buttons after data is changed externally from the angular world
}

function SettingsSectionList(props: SectionContainerBaseProps & BaseReactSettingsCtrlProps) {
  wireFormikCallbacks(props);
  const {sections, ...rest} = props;

  const unregisteredSections = props.sections?.filter(key => !(key in SettingsComponentRegistry));

  if(unregisteredSections?.length > 0) {
    throw new Error(`Tried to render unregistered settings sections: ${unregisteredSections}`);
  }

  return <> {props.sections?.map((key, index) => {
    const Section = SettingsComponentRegistry[key];
    return <Section.component key={index} {...rest}/>;
  })}
  </>;
}

export function SettingsFormikContainer<Props extends SectionContainerBaseProps>(props: SectionContainerBaseProps
  & BaseReactSettingsCtrlProps
  & {
    initErrors?: FormikErrors<Props>;
    initTouched?: FormikTouched<Props>;
    }) {
  // filter values down to migrated settings.
  const {sections, ...rest} = props;

  const initValues = {
    settings: props.settings,
    availability: (props as any).availability
  };

  return <Formik initialValues={ initValues as any} // typescript is being difficult
                 onSubmit={handleSubmit}
                 validateOnMount={true}
                 initialTouched={props.initTouched}
                 initialErrors={props.initErrors}>
      <SettingsSectionList {...props}/>
  </Formik>;
}
